import { Box, EmptyState, HighlightIcon } from '@dev-spendesk/grapes';
import { add, type MonetaryValue } from 'ezmoney';
import { useDispatch } from 'react-redux';

import { useFeature } from 'common/hooks/useFeature';
import { useNotifications } from 'modules/app/notifications';
import { PaymentContext } from 'modules/invoices/hooks';
import {
  selectors,
  useQuotes,
  useSelectorWithContext,
} from 'modules/invoices/transfer';
import { useModalState } from 'src/core/common/hooks/useModalState';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { useCompanyCurrency } from 'src/core/modules/app/hooks/useCompanyCurrency';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { InvoiceInconsistencyItemSummary } from 'src/core/modules/invoices/components/InvoiceInconsistencyItemSummary';
import { useVerificationOfPayeeCheck } from 'src/core/modules/verification-of-payee/apis';
import { ConfirmInconsistencyModal } from 'src/core/modules/verification-of-payee/components/ConfirmInconsistencyModal';
import { type InvoiceInconsistencyItem } from 'src/core/modules/verification-of-payee/models';
import { isVerificationOfPayeeResultInvalid } from 'src/core/modules/verification-of-payee/utils';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import {
  useConfirmTransfersTrackingEvents,
  useVerificationOfPayeeInconsistencyItems,
} from '../../hooks';
import { useIsIwtEnabled } from '../../hooks/api/useIsIwtEnabled';
import { actions } from '../../redux';
import { QuoteBreakdown } from '../QuoteBreakdown';
import { TransferCancelModal } from '../TransferCancelModal';
import { TransferConfirmationModal } from '../TransferConfirmationModal';
import { CancelTransfersButton } from './CancelTransfersButton';
import { ConfirmTransfersButton } from './ConfirmTransfersButton';
import { WalletBalance } from './WalletBalance';

const handleVerificationOfPayeeError = (
  accountHolderName: string | undefined,
  t: ReturnType<typeof useTranslation>['t'],
  dangerNotif: ReturnType<typeof useNotifications>['dangerNotif'],
) => {
  dangerNotif(
    t('verificationOfPayee.errors.technicalError'),
    accountHolderName
      ? t('verificationOfPayee.errors.impactedPayee', { accountHolderName })
      : undefined,
  );
};

export function ConfirmationDetails({
  isFinanceController,
}: {
  isFinanceController: boolean;
}) {
  const { t } = useTranslation('global');
  const dispatch = useDispatch();
  const { type } = PaymentContext.useContext();
  const confirmTransfersTrackingEvents = useConfirmTransfersTrackingEvents();
  const { dangerNotif } = useNotifications();
  const isVerificationOfPayeeEnabled = useFeature(
    FEATURES.TMP_VERIFICATION_OF_PAYEE_EEA,
  );
  const checkVerificationOfPayee = useVerificationOfPayeeCheck();

  const companyId = useCompanyId();

  const companyCurrency = useCompanyCurrency();

  const isIwtEnabled = useIsIwtEnabled();

  const selectedTransfers = useSelectorWithContext(
    selectors.selectTransfersSelected,
  );

  const invalidVoPTransfers = useSelectorWithContext(
    selectors.selectTransfersWithInvalidVerificationOfPayee,
  );
  const verificationOfPayeeInconsistencyItems =
    useVerificationOfPayeeInconsistencyItems(invalidVoPTransfers);

  const confirmTransfersModal = useModalState({
    onOpen() {
      confirmTransfersTrackingEvents.onConfirmTransfersConfirmationModalOpened({
        transfers: selectedTransfers,
      });
    },
    onClose() {
      confirmTransfersTrackingEvents.onConfirmTransfersConfirmationModalClosed({
        transfers: selectedTransfers,
      });
    },
  });
  const cancelTransfersModal = useModalState({
    onOpen() {
      confirmTransfersTrackingEvents.onConfirmTransfersRejectionModalOpened({
        transfers: selectedTransfers,
      });
    },
    onClose() {
      confirmTransfersTrackingEvents.onConfirmTransfersRejectionModalClosed({
        transfers: selectedTransfers,
      });
    },
  });
  const inconsistencyModal = useModalState({});

  const { totalAmountWithQuotes } = useQuotes({
    companyId,
    selectedTransfers,
    companyCurrency,
  });

  const handleConfirmationTransfers = async () => {
    if (isVerificationOfPayeeEnabled) {
      const verificationOfPayeeResultsSettled = await Promise.allSettled(
        selectedTransfers.map((transfer) => {
          const counterparty = transfer.bill.counterparty;
          if (
            !counterparty.bankAddress?.iban ||
            !counterparty.bankAddress?.accountHolderName
          ) {
            throw new Error('No bank address found for transfer');
          }
          return checkVerificationOfPayee({
            iban: counterparty.bankAddress.iban,
            name: counterparty.bankAddress.accountHolderName,
            refresh: true,
          });
        }),
      );

      const firstFailure = verificationOfPayeeResultsSettled.find(
        (result) => result.status === 'rejected',
      );
      if (firstFailure) {
        const failedTransferIndex =
          verificationOfPayeeResultsSettled.indexOf(firstFailure);
        const accountHolderName =
          selectedTransfers[failedTransferIndex]?.bill.counterparty.bankAddress
            ?.accountHolderName;

        handleVerificationOfPayeeError(accountHolderName, t, dangerNotif);
        return;
      }

      const verificationOfPayeeResults = verificationOfPayeeResultsSettled
        .filter((result) => result.status === 'fulfilled')
        .map(
          (result) =>
            (result as PromiseFulfilledResult<typeof result.value>).value,
        );

      const verificationOfPayeePayload = selectedTransfers.map(
        (transfer, index) => {
          return {
            transferId: transfer.id,
            verificationOfPayee: {
              initialResult: verificationOfPayeeResults[index],
              isIgnored: false,
            },
          };
        },
      );

      dispatch(
        actions.updateTransferVerificationOfPayee({
          transfers: verificationOfPayeePayload.map((transfer) => ({
            transferId: transfer.transferId,
            verificationOfPayee: transfer.verificationOfPayee,
          })),
          type,
        }),
      );

      const hasSomeInvalidVerificationOfPayee = verificationOfPayeeResults.some(
        (verificationOfPayeeResult) =>
          isVerificationOfPayeeResultInvalid(
            verificationOfPayeeResult?.matchingResult,
          ),
      );

      if (!hasSomeInvalidVerificationOfPayee) {
        confirmTransfersModal.open();
        return;
      }
      inconsistencyModal.open();
      return;
    }
    confirmTransfersModal.open();
  };

  const onCancelInconsistencyModal = () => {
    inconsistencyModal.close();
    cancelTransfersModal.open();
  };

  const onContinueInconsistencyModal = () => {
    inconsistencyModal.close();
    confirmTransfersModal.open();
  };

  const onCloseInconsistencyModal = () => {
    inconsistencyModal.close();
  };

  const resetTransfersSelection = () => {
    dispatch(actions.resetTransfersSelection({ type }));
  };

  // Reduce the total monetary value by currency to a total and an array of the
  // separate amounts in order to count the number.
  const totalAmountsByCurrency = Object.values(
    selectedTransfers.reduce<
      Record<string, { transfers: MonetaryValue[]; total: MonetaryValue }>
    >((accumulator, current) => {
      const value = accumulator[current.amount.currency];

      if (value) {
        accumulator[current.amount.currency].transfers.push(current.amount);
        accumulator[current.amount.currency].total = add(
          current.amount,
          value.total,
        );
      } else {
        accumulator[current.amount.currency] = {
          transfers: [current.amount],
          total: current.amount,
        };
      }

      return accumulator;
    }, {}),
  );

  if (!selectedTransfers.length) {
    return (
      <div className="border-border-default h-full border border-y-[0px] border-r-[0px] border-l-[1px] border-solid">
        <section className="sticky top-[328px]">
          <EmptyState
            iconName="cursor-arrow-rays"
            title={t('invoices.transfer.confirm.emptyState.title')}
            subtitle=""
          />
        </section>
      </div>
    );
  }

  return (
    <section className="sticky top-12">
      <div className="flex flex-col gap-24">
        <h2 className="heading-l">{t('invoices.transfer.confirm.title')}</h2>

        <div className="flex flex-col gap-12">
          {totalAmountsByCurrency.length > 0 ? (
            totalAmountsByCurrency.map((it) => (
              <div key={it.total.currency} className="flex justify-between">
                <p className="text-content-secondary-bg-primary">
                  {t('invoices.transfer.confirm.paymentsSelected', {
                    count: it.transfers.length,
                    currency: it.total.currency,
                  })}
                </p>

                <p className="text-content-primary">
                  {formatMonetaryValue(it.total)}
                </p>
              </div>
            ))
          ) : (
            <p className="text-content-secondary-bg-primary">
              {t('invoices.transfer.confirm.noPaymentsSelected')}
            </p>
          )}
        </div>

        {isIwtEnabled ? <QuoteBreakdown /> : null}

        <div className="flex justify-between gap-8">
          <p className="text-content-primary-bg-primary body-xl">
            {t('quoteBreakdown.outgoingAmount')}
          </p>

          <div className="flex items-end gap-8">
            <span className="text-decorative-icon body-l">
              {t('quoteBreakdown.total')}:
            </span>

            <span className="font-600 body-xl">
              {formatMonetaryValue(totalAmountWithQuotes)}
            </span>
          </div>
        </div>

        <Box className="flex items-center gap-12" variant="secondary">
          <HighlightIcon name="wallet" size={40} variant="purple" />

          <WalletBalance />
        </Box>

        <div className="flex justify-stretch gap-16">
          <CancelTransfersButton />

          {isFinanceController ? (
            <ConfirmTransfersButton onClick={handleConfirmationTransfers} />
          ) : (
            false
          )}
        </div>
      </div>
      {inconsistencyModal.isOpen && isVerificationOfPayeeEnabled && (
        <ConfirmInconsistencyModal<InvoiceInconsistencyItem>
          isOpen
          items={verificationOfPayeeInconsistencyItems}
          renderItemSummary={(item) => (
            <InvoiceInconsistencyItemSummary item={item} />
          )}
          onCancel={onCancelInconsistencyModal}
          onContinue={onContinueInconsistencyModal}
          onClose={onCloseInconsistencyModal}
        />
      )}
      <TransferConfirmationModal
        transfers={selectedTransfers}
        isOpen={confirmTransfersModal.isOpen}
        onClose={confirmTransfersModal.close}
        onConfirm={resetTransfersSelection}
      />
      <TransferCancelModal
        transfers={selectedTransfers}
        isOpen={cancelTransfersModal.isOpen}
        onClose={cancelTransfersModal.close}
        onConfirm={resetTransfersSelection}
      />
    </section>
  );
}
